import React, { useState } from 'react';
import { Stack, Chip, IconButton, Menu, MenuItem } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useMutation } from '@apollo/client/react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  RecurringTaskFragmentFragment,
  DeleteRecurringTaskDocument,
  DeleteRecurringTaskMutation,
  DeleteRecurringTaskMutationVariables,
  UndeleteRecurringTaskDocument,
  UndeleteRecurringTaskMutation,
  UndeleteRecurringTaskMutationVariables,
  SuspendRecurringTaskDocument,
  SuspendRecurringTaskMutation,
  SuspendRecurringTaskMutationVariables,
  UnsuspendRecurringTaskDocument,
  UnsuspendRecurringTaskMutation,
  UnsuspendRecurringTaskMutationVariables,
  RecurringTaskFrequency,
} from '../generated/graphql';
import { useApp } from './app-context';
import { useAlert } from '@dwelvio/react-alert';

interface RecurringTasksListProps {
  tasks: RecurringTaskFragmentFragment[];
  onRefresh: () => void;
  onEdit?: (task: RecurringTaskFragmentFragment) => void;
  disabled?: boolean;
  showSecondLine?: boolean;
}

const frequencyLabels: Record<RecurringTaskFrequency, string> = {
  W: 'Týždenne',
  W2: 'Každé 2 týždne',
  W4: 'Každé 4 týždne',
  M: 'Mesačne',
  M2: 'Každé 2 mesiace',
  M3: 'Každé 3 mesiace',
  M4: 'Každé 4 mesiace',
  M6: 'Polročne',
  Y: 'Ročne',
  Y2: 'Každé 2 roky',
  Y3: 'Každé 3 roky',
  Y4: 'Každé 4 roky',
  Y5: 'Každých 5 rokov',
};

export function RecurringTasksList({ tasks, onRefresh, onEdit, disabled, showSecondLine }: RecurringTasksListProps) {
  const app = useApp();
  const { show } = useAlert();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTask, setSelectedTask] = useState<RecurringTaskFragmentFragment | null>(null);

  const [deleteRecurringTask] = useMutation<DeleteRecurringTaskMutation, DeleteRecurringTaskMutationVariables>(
    DeleteRecurringTaskDocument,
    {
      onCompleted: () => {
        show.info('Pravidelná úloha bola odstránená');
        onRefresh();
      },
      onError: (e: Error) => show.error('Nepodarilo sa odstrániť pravidelnú úlohu', e.message),
    },
  );

  const [undeleteRecurringTask] = useMutation<UndeleteRecurringTaskMutation, UndeleteRecurringTaskMutationVariables>(
    UndeleteRecurringTaskDocument,
    {
      onCompleted: () => {
        show.info('Pravidelná úloha bola obnovená');
        onRefresh();
      },
      onError: (e: Error) => show.error('Nepodarilo sa obnoviť pravidelnú úlohu', e.message),
    },
  );

  const [suspendRecurringTask] = useMutation<SuspendRecurringTaskMutation, SuspendRecurringTaskMutationVariables>(
    SuspendRecurringTaskDocument,
    {
      onCompleted: () => {
        show.info('Pravidelná úloha bola pozastavená');
        onRefresh();
      },
      onError: (e: Error) => show.error('Nepodarilo sa pozastaviť pravidelnú úlohu', e.message),
    },
  );

  const [unsuspendRecurringTask] = useMutation<UnsuspendRecurringTaskMutation, UnsuspendRecurringTaskMutationVariables>(
    UnsuspendRecurringTaskDocument,
    {
      onCompleted: () => {
        show.info('Pravidelná úloha bola obnovená');
        onRefresh();
      },
      onError: (e: Error) => show.error('Nepodarilo sa obnoviť pravidelnú úlohu', e.message),
    },
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, task: RecurringTaskFragmentFragment) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTask(null);
  };

  const handleSuspend = () => {
    if (selectedTask) {
      suspendRecurringTask({ variables: { recurringTaskId: selectedTask.id } });
    }
    handleMenuClose();
  };

  const handleUnsuspend = () => {
    if (selectedTask) {
      unsuspendRecurringTask({ variables: { recurringTaskId: selectedTask.id } });
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedTask) {
      deleteRecurringTask({ variables: { recurringTaskId: selectedTask.id } });
    }
    handleMenuClose();
  };

  const handleUndelete = () => {
    if (selectedTask) {
      undeleteRecurringTask({ variables: { recurringTaskId: selectedTask.id } });
    }
    handleMenuClose();
  };

  const columns = React.useMemo((): GridColDef<RecurringTaskFragmentFragment>[] => {
    return [
      {
        display: 'flex',
        field: 'title',
        headerName: 'Názov',
        width: 250,
        flex: 1,
        renderCell: (t) => {
          const secondary = showSecondLine ? (t.row.unit?.name ?? t.row.contract?.title ?? null) : null;
          return (
            <Stack>
              <span>{t.value}</span>
              {secondary && <span style={{ color: 'gray', fontSize: '0.8em' }}>{secondary}</span>}
            </Stack>
          );
        },
      },
      {
        field: 'nextDate',
        headerName: 'Nasledujúca',
        width: 140,
        valueFormatter: (value: string) => app.formatDate(value),
      },
      {
        field: 'frequency',
        headerName: 'Frekvencia',
        width: 150,
        valueFormatter: (value: RecurringTaskFrequency) => frequencyLabels[value] || value,
      },

      {
        field: 'dueDays',
        headerName: 'Dni na splnenie',
        width: 110,
        align: 'center',
      },
      {
        field: 'suspended',
        headerName: 'Stav',
        width: 120,
        renderCell: (params) => {
          if (params.row.deletedAt) {
            return <Chip label="Odstránená" color="error" size="small" />;
          }
          return params.value ? (
            <Chip label="Pozastavená" color="warning" size="small" />
          ) : (
            <Chip label="Aktívna" color="success" size="small" />
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        renderCell: (params) => (
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)} disabled={disabled}>
            <MoreVertIcon />
          </IconButton>
        ),
      },
    ];
  }, [app, disabled]);

  return (
    <Stack spacing={2}>
      <DataGrid
        rows={tasks}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
          sorting: {
            sortModel: [{ field: 'nextDate', sort: 'asc' }],
          },
        }}
        pageSizeOptions={[10, 20, 50]}
        disableRowSelectionOnClick
        onRowClick={(params) => {
          if (onEdit && !disabled) {
            onEdit(params.row);
          }
        }}
        autoHeight
        sx={{
          '& .MuiDataGrid-row': {
            cursor: onEdit && !disabled ? 'pointer' : 'default',
          },
        }}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedTask && !selectedTask.deletedAt && (
          <>
            {selectedTask.suspended ? (
              <MenuItem onClick={handleUnsuspend}>
                <PlayArrowIcon fontSize="small" sx={{ mr: 1 }} />
                Obnoviť
              </MenuItem>
            ) : (
              <MenuItem onClick={handleSuspend}>
                <PauseIcon fontSize="small" sx={{ mr: 1 }} />
                Pozastaviť
              </MenuItem>
            )}
            <MenuItem onClick={handleDelete}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Odstrániť
            </MenuItem>
          </>
        )}
        {selectedTask && selectedTask.deletedAt && (
          <MenuItem onClick={handleUndelete}>
            <RestoreIcon fontSize="small" sx={{ mr: 1 }} />
            Obnoviť
          </MenuItem>
        )}
      </Menu>
    </Stack>
  );
}
